// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';
import {
  zIndex,
  spacing,
  fontWeight,
  fontSize,
  mediaMin,
  mediaMax,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import {
  white,
  black,
  gray04,
  primary,
  gray01,
} from 'presentations/views/styles/color';
import CalendarPageTemplate from 'presentations/views/components/templates/apps/CalendarPageTemplate';
import CalendarList from 'presentations/views/components/organisms/apps/CalendarList';
import MonthCalendar from 'presentations/views/components/organisms/apps/MonthCalendar';
import { CalendarArray } from 'usecases/app_calendar';
import { Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import Breadcrumb from 'presentations/routes/breadcrumb';
import Link from 'presentations/views/components/atoms/Link';
import {
  appCalendarMonthFilterPath,
  appCalendarMonthPath,
  appCalendarYearFilterPath,
  appCalendarYearPath,
} from 'presentations/routes/path';
import { getDateTime } from 'infras/datetime';
import { imagePath } from 'presentations/assets';
import Head from 'next/head';

interface Props {
  mainCalendar: CalendarArray;
  calendars: CalendarArray[];
  calendarName: string;
  year: number;
  month: number;
  filter?: string;
  gengo: Gengo;
  holidays: any;
  holidayMasters: any;
  routes?: Breadcrumb[];
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const date = new Date(props.year, props.month - 1, 1);
  const nextMonth = getDateTime({ year: props.year, month: props.month }).plus({
    month: 1,
  });
  const gengo = t('gengoTitleKana', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
    kana: props.gengo.kana,
  });

  const mainImage = `apps/calendar/mv/${props.month}.jpg`;
  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === props.year && today.getMonth() === props.month;
  const currentYearUrl = props.filter
    ? appCalendarYearFilterPath(props.year, props.filter)
    : appCalendarYearPath(props.year);
  const nextMonthUrl = props.filter
    ? appCalendarMonthFilterPath(nextMonth.year, nextMonth.month, props.filter)
    : appCalendarMonthPath(nextMonth.year, nextMonth.month);
  const title = t(isCurrentMonth ? `currentMonthTitle` : 'monthTitle', {
    date,
    calendarName: props.calendarName,
  });
  const image = imagePath(mainImage, { height: 280 });

  return (
    <CalendarPageTemplate
      routes={props.routes}
      filter={props.filter}
      month={props.month}
      year={props.year}
      holidayMasters={props.holidayMasters}
      holidays={props.holidays}
    >
      <Head>
        <link rel="preload" as="image" href={image} />
      </Head>
      <Cover>
        <CurrentMonthContainer>
          <CurrentMonthTitleContainer>
            <CurrentMonthTitle>
              {title}
              <PageDescription>{gengo}</PageDescription>
            </CurrentMonthTitle>
            <NextMonthLink href={nextMonthUrl}>{`${t('nextMonth', {
              date: nextMonth.toJSDate(),
            })}`}</NextMonthLink>
          </CurrentMonthTitleContainer>

          <ThemeProvider theme={{ appearance: 'dark' }}>
            <MonthCalendar weeks={props.mainCalendar} filter={props.filter} />
          </ThemeProvider>
        </CurrentMonthContainer>
      </Cover>
      {/* <StickyAdContainer>
        <StickyAdBanner productType={ProductType.Calendar} isHideClose />
      </StickyAdContainer> */}
      <MainContents>
        <Content>
          <ListContainer>
            <Headline>
              {t('allYearlyCalendarTitle', {
                date,
                calendarName: props.calendarName,
              })}
            </Headline>
            <CalendarList
              year={props.year}
              calendars={props.calendars}
              filter={props.filter}
            />
          </ListContainer>

          <ListContainer>
            <Headline>
              {t('allCalendarLinkTitle', { year: props.year })}
            </Headline>
            <LinkListContainer>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                const y = props.year + i;
                if (y > 2030) return;
                const yearUrl = props.filter
                  ? appCalendarYearFilterPath(y, props.filter)
                  : appCalendarYearPath(y);
                return (
                  <LinkListInner key={y}>
                    <LinkListTitle>
                      <LinkListItemLink href={yearUrl}>
                        {t('date.year', {
                          date: new Date(y, 1, 1),
                        })}
                      </LinkListItemLink>
                    </LinkListTitle>
                    <LinkListContent>
                      <LinkList>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                          (month) => {
                            const monthUrl = props.filter
                              ? appCalendarMonthFilterPath(
                                  y,
                                  month,
                                  props.filter,
                                )
                              : appCalendarMonthPath(y, month);
                            return (
                              <LinkListItem key={month}>
                                <LinkListItemLink href={monthUrl}>
                                  {t('date.month', {
                                    date: new Date(y, month - 1, 1),
                                  })}
                                </LinkListItemLink>
                              </LinkListItem>
                            );
                          },
                        )}
                      </LinkList>
                    </LinkListContent>
                  </LinkListInner>
                );
              })}
            </LinkListContainer>
            <LinkListMore>
              <LinkListItemLink href={'/apps/calendar/table'}>
                {t('allCalendarLink')}
              </LinkListItemLink>
            </LinkListMore>
          </ListContainer>
        </Content>
      </MainContents>
    </CalendarPageTemplate>
  );
}

const LinkListMore = styled.div`
  margin-top: ${spacing(12)};
  ${mediaMin.tablet} {
    margin-top: ${spacing(16)};
  }
`;

const LinkListContainer = styled.dl`
  ${mediaMin.tablet} {
    padding: ${spacing(24)} 0 ${spacing(12)};
  }
`;
const LinkListTitle = styled.dt`
  font-size: ${fontSize(14)};
  flex-shrink: 0;
  padding-top: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    padding-top: 0;
    padding-right: 1em;
  }
`;
const LinkListInner = styled.div`
  border-top: 1px solid ${gray04};
  ${mediaMin.tablet} {
    border-top: 0;
    font-size: ${fontSize(16)};
    display: flex;
    :not(:first-of-type) {
      margin-top: ${fontSize(12)};
    }
  }
`;
const LinkListContent = styled.dd`
  width: 100%;
  padding: ${spacing(16)} 0 0;
  ${mediaMin.tablet} {
    padding: 0;
  }
`;
const LinkList = styled.ul`
  display: flex;
  flex-wrap: wrap;
`;
const LinkListItem = styled.li`
  font-size: ${fontSize(14)};
  padding-right: ${spacing(24)};
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
    margin-bottom: ${spacing(12)};
  }
`;
const LinkListItemLink = styled(Link)`
  color: ${primary};
  ${mediaMax.tablet} {
    display: block;
  }
  &:hover {
    color: ${primary};
    text-decoration: underline;
  }
`;

const MainContents = styled.main`
  padding: ${spacing(40)} ${spacing(16)};
  ${mediaMax.tablet} {
    padding: ${spacing(24)} 0;
  }
`;
const Content = styled.div`
  color: ${black};
  ${mediaMin.tablet} {
    max-width: 1100px;
    margin: 0 auto;
  }
`;
const Headline = styled.h2`
  font-size: ${fontSize(32)};
  text-align: center;
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    font-size: ${fontSize(32)};
  }
`;

const ListContainer = styled.div`
  margin-bottom: ${spacing(48)};
  padding: ${spacing(16)} 0;
  ${mediaMax.tablet} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(32)};
  }
`;

const Cover = styled.div`
  border-radius: ${borderRadius(16)};
  position: relative;
  background: #111111;
  overflow: hidden;
  ${mediaMax.desktop} {
    border-radius: 0;
  }
  ${mediaMin.tablet} {
    display: flex;
    flex-flow: row wrap;
  }
`;
const CoverImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  ${mediaMax.tablet} {
    height: calc(100vw / 12 * 5);
    width: 100%;
  }
  ${mediaMin.tablet} {
    width: calc(100% / 12 * 5);
  }
  ${mediaMin.desktop} {
    min-height: 560px;
  }
`;
const PageDescription = styled.span`
  display: block;
  color: ${white};
  font-size: ${fontSize(13)};
  font-weight: 600;
  line-height: 1.6;
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
  }
  z-index: 2;
`;
const CurrentMonthContainer = styled.div`
  padding: 0 ${spacing(16)} ${spacing(16)};
  color: ${white};
  ${mediaMin.tablet} {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: ${spacing(24)};
    width: calc(100% / 12 * 5);
  }
  ${mediaMin.desktop} {
    padding: ${spacing(48)};
  }
`;
const CurrentMonthTitleContainer = styled.div`
  margin-bottom: ${spacing(32)};
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
    margin-top: ${spacing(24)};
    font-size: ${fontSize(18)};
  }
`;
const CurrentMonthTitle = styled.h1`
  color: ${white};
`;

const NextMonthLink = styled(Link)`
  display: block;
  color: ${white};
  border-radius: ${borderRadius(16)};
  background-color: ${gray01};
  padding: ${spacing(4)} ${spacing(16)};
  font-size: ${fontSize(14)};
`;

const MainVisual = styled.div<{ image: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: right;
  text-shadow: rgba(0, 0, 0, 0.5) 0px 0px 30px;
  z-index: ${zIndex(100)};
  ${mediaMax.tablet} {
    height: calc(100vw / 12 * 5);
    padding: ${spacing(16)};
  }
  ${mediaMin.tablet} {
    width: calc(100% / 12 * 5);
    padding: ${spacing(24)};
  }
  ${mediaMin.desktop} {
    padding: ${spacing(48)};
    min-height: 560px;
  }
  ${({ image }) =>
    image &&
    css`
      background-image: url(${image});
      background-repeat: no-repeat;
      background-size: cover;
    `};
  :before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(17, 17, 17, 0) 60%, #111111 100%);
    z-index: -${zIndex(100)};
  }
`;

const StickyAdContainer = styled.div`
  position: sticky;
  top: 48px;
  left: 0;
  right: 0;
  ${mediaMin.tablet} {
    display: none;
  }
`;
